// downloadUtils.js
import html2pdf from "html2pdf.js";
import { Document, Packer, Paragraph } from "docx";
import { getResultDataForLegislation, getTranslatedText } from "./kumuHelper.js"
import { ResultType } from "../../constants.js";
import { stripHtml } from "../../components/ResultCard.js";
// --------------------------------------------------
// HELPERS
// --------------------------------------------------
// const stripHtml = (html) => html.replace(/<[^>]*>?/gm, "");

// Centralized access to legislation attributes
const getLegislationAttributes = (lg) =>
  lg?.legislation?.value?.attributes || {};

const getLegAtt = (lg) => lg?.value?.attributes || {};

const getAttr = (lg, key, lang = "en") => {
  const attrs = getLegAtt(lg);

  const value =
    lang === "fr"
      ? attrs[`${key} fr`] ?? attrs[key]
      : attrs[key];

  return value || (lang === "fr" ? "Non disponible" : "Not available");
};

const getLegislationCode = (lg, lang = "en") => {
  const attrs = getLegislationAttributes(lg);
  return (
    attrs["reference id"] ||
    (lang === "fr" ? "Non disponible" : "Not available")
  );
};

const reasonCount = (reason) =>
  (reason?.match(/Reason approval may be required/gi) || []).length;

// Helper function to format date as YYYY-MM-DD
const getFormattedDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// --------------------------------------------------
// HEADERS & NOTICES
// --------------------------------------------------
const headerEn = `
  <div>
    <div style="text-align:center; margin-bottom:20px;">
      <img src="/static/graphics/Natural_Resources_Canada.png"
        alt="Natural Resources Canada / Ressources naturelles Canada"
        style="max-width:100%; height:auto;" />
    </div>
    <h1>Mine Permit Navigator</h1>
  </div>
`;

const headerFr = `
  <div>
    <div style="text-align:center; margin-bottom:20px;">
      <img src="/static/graphics/Natural_Resources_Canada.png"
        alt="Ressources naturelles Canada / Natural Resources Canada"
        style="max-width:100%; height:auto;" />
    </div>
    <h1>Navigateur de permis miniers</h1>
  </div>
`;

const noticeEn = `
  <div style="background:#e6f9ff; border:1px solid #99d6eb; padding:10px;">
    <p><strong>Please note:</strong> the content of this PDF is displayed in the language of the original submission and has not been altered.</p>
  </div>
`;

const noticeFr = `
  <div style="background:#e6f9ff; border:1px solid #99d6eb; padding:10px;">
    <p><strong>Veuillez noter :</strong> le contenu de ce PDF est affiché dans la langue de la demande originale et n'a pas été modifié.</p>
  </div>
`;

// --------------------------------------------------
// DISCLAIMERS (FULL TEXT)
// --------------------------------------------------
const disclaimerEn = `
  <section style="margin-top:40px; font-size:14px; line-height:1.6;">
    <h2>Disclaimer</h2>

    <p>
      The Mine Permit Navigator is for information purposes only. It is not a substitute for any federal legislation and regulations. 
      The federal legislation and regulations prevail in the event of any inconsistency with this tool. 
      In case of any doubt, do not hesitate to reach out to the responsible federal authority, which can be found in the
      <a href="https://mpn-tool-outil-npm.nrcan-rncan.cloud-nuage.canada.ca/en/faq" target="_blank" style="color:#0000EE; text-decoration:underline;">Frequently Asked Questions</a>.
    </p>

    <p>
      The Mine Permit Navigator includes federal legislation only, excluding specific requirements for projects north of the 60th parallel. 
      It is recommended that you review legislation specific to working north of the 60th parallel. 
      It is also recommended that you review provincial and territorial legislation, as your project may be subject to additional requirements at the provincial and territorial level.
    </p>
    <p>
      The Mine Permit Navigator does not include any information regarding the duty to consult with Indigenous Peoples in Canada. 
      The federal government has a legal duty to consult when it contemplates conduct that will create adverse impacts to Indigenous and Treaty rights. 
      A duty to consult could arise in the context of regulatory project approvals, permit, licensing and authorization, operational choices, policy creation, negotiations, and other related actions. 
      Furthermore, statutory requirements, provisions in land claim agreements, and consultation processes may all create the duty to consult. 
      Proponents are responsible for engaging with the relevant federal departments that oversee any licences or permits they seek, to ensure they are fully informed about any potential duty to consult and can plan early engagement and consultation processes accordingly.
    </p>
    <p>
      It is encouraged that early and meaningful engagement take place to ensure that potentially impacted Indigenous groups and other parties are informed of potential projects, actions or regulatory permits, to build relationships, and to understand and respond to any concerns an Indigenous group or other groups may have. 
      If a formal duty to consult exists, the Crown consultation process will be undertaken to ensure an understanding of how the project or issuance of a permit may impact Indigenous rights and interests, and whether these impacts can be avoided, mitigated, or, where appropriate, accommodated in order to fully inform a decision on the proposed project or required licence or permit. 
      Meaningful and sufficient consultation can contribute to reconciliation by fostering relationships with Indigenous Peoples, creating the basis for future work together.
    </p>
  </section>
`;

const disclaimerFr = `
  <section style="margin-top:40px; font-size:14px; line-height:1.6;">
    <h2>Avis de non-responsabilité</h2>

    <p>
      Le Navigateur de permis miniers est fourni à titre informatif uniquement. 
      Il ne remplace en aucun cas les lois et règlements fédéraux. En cas de divergence entre cet outil et la législation et les règlements fédéraux, ce sont ces derniers qui prévaudront. 
      En cas de doute, n'hésitez pas à contacter les autorités fédérales responsables, indiquées dans la section
      <a href="https://mpn-tool-outil-npm.nrcan-rncan.cloud-nuage.canada.ca/fr/faq" target="_blank" style="color:#0000EE; text-decoration:underline;">questions fréquemment posées</a>.
    </p>

    <p>
      Le Navigateur de permis miniers ne comprend que la législation fédérale, à l'exclusion des exigences spécifiques pour les projets au nord du 60e parallèle. 
      Il est recommandé d'examiner les lois spécifiques applicables au travail au nord du 60e parallèle. 
      Il est également recommandé de consulter la législation provinciale et territoriale, car votre projet pourrait être soumis à des exigences supplémentaires au niveau provincial et territorial.
    </p>

    <p>
      Le Navigateur de permis miniers ne fournit aucune information concernant l'obligation de consulter les peuples autochtones au Canada. 
      Le gouvernement fédéral a l'obligation légale de consulter lorsqu'il envisage des mesures susceptibles d'avoir des effets négatifs sur les droits ancestraux ou issus de traités des peuples autochtones. 
      L'obligation de consulter peut notamment s'appliquer dans le cadre de l'approbation de projets réglementaires, de la délivrance de permis, de licences ou d'autorisations, de choix opérationnels, de l'élaboration de politiques, de négociations et d'autres mesures connexes. 
      De plus, les règlements sur les textes réglementaires, des dispositions prévues dans des accords sur des revendications territoriales et des processus de consultation peuvent également donner lieu à une obligation de consulter. 
      Les promoteurs sont responsables d'entrer en communication avec les ministères fédéraux concernés qui sont responsables des licences ou permis demandés, afin de s'assurer qu'ils sont pleinement informés de toute obligation éventuelle de consulter et qu'ils puissent planifier en conséquence des activités de mobilisation et de consultation précoce.
    </p>

    <p>
      Il est recommandé d'engager tôt un dialogue significatif afin que les groupes autochtones potentiellement touchés et d'autres parties soient informés des projets, mesures ou permis réglementaires envisagés, de favoriser des relations de confiance et de répondre aux préoccupations soulevées. 
      Si une obligation officielle de consultation existe, le processus de consultation de la Couronne sera entrepris pour évaluer les répercussions du projet ou du permis sur les droits et intérêts autochtones, et déterminer comment éviter, atténuer ou, le cas échéant, compenser ces répercussions.
      Une consultation suffisante et significative peut contribuer à la réconciliation en renforçant les relations avec les peuples autochtones et en jetant les bases d'une collaboration future.
    </p>
  </section>
`;

// Helper function to make links clickable with proper styling
const makeLinksClickable = (htmlContent) => {
  if (!htmlContent) return htmlContent;
  
  // Add styling to existing <a> tags
  return htmlContent.replace(
    /<a\s+([^>]*href\s*=\s*["']([^"']+)["'][^>]*)>/gi,
    '<a $1 style="color:#0000EE; text-decoration:underline;" target="_blank">'
  );
};

export const getStatus = (status) => {
    if(status === ResultType.REQUIRED) return 'You may have responsibilities'
    if(status === ResultType.LEARN) return 'Learn More'
    return status
  }

const getAnswer = (result , lang) => {
    if (!result) return lang === 'fr' ? "Non disponible: questions non répondues" :"Not available, questions not answered";

    if (result === "Required") {
      return lang === 'fr' ? "Vous pourriez avoir des responsabilités" : "You may have responsibilities";
    }

    if (result === "Learn") {
      return lang === 'fr' ? "Vous n'avez probablement pas de responsabilités" : "You likely do not have responsibilities";
    }

    return lang === 'fr' ? "Non disponible: questions non répondues" :"Not available, questions not answered";
};

// --------------------------------------------------
// RESULT CARD TEMPLATES
// --------------------------------------------------
const resultCardHtmlEn = (result, lg, lang) => `
  <div style="border:1px solid #ccc; border-radius:10px; padding:20px; margin-bottom:20px; page-break-inside:avoid;">
    <h2>${getTranslatedText(lang, lg.name)}</h2>
    <p style="font-weight:bold; color:#d3080c;">${getAnswer(lg.status, lang)}</p>

    <h3>Result(s)</h3>
    <div>${makeLinksClickable(lg.reason) || "Not available"}</div>

    <h3>Contact Information</h3>
    <div>${makeLinksClickable(getAttr(result, "contact"))}</div>

    <h3>How to Apply or Report</h3>
    <div>${makeLinksClickable(getAttr(result, "where to apply"))}</div>

    <h3>Application and Regulatory Guidance</h3>
    <div>${makeLinksClickable(getAttr(result, "guidance"))}</div>

    <h3>Timelines</h3>
    <div>${makeLinksClickable(getAttr(result, "timelines"))}</div>

    <h3>Application Examples</h3>
    <div>${makeLinksClickable(getAttr(result, "examples"))}</div>

    <h3>Program Website</h3>
    <div>${makeLinksClickable(getAttr(result, "program website"))}</div>

    <h3>Geographical Legislation</h3>
    <p>${makeLinksClickable(getAttr(result, "geographical legislation"))}</p>

    <h3>Your Answers to the Guided Search Questions</h3>
    <ol>
      ${lg.history?.map((q) => `<li>${stripHtml(q)}</li>`).join("") || "<li>No questions</li>"}
    </ol>
  </div>
`;

const resultCardHtmlFr = (result, lg, lang) => `
  <div style="border:1px solid #ccc; border-radius:10px; padding:20px; margin-bottom:20px; page-break-inside:avoid;">
    <h2>${getTranslatedText(lang, lg.name)}</h2>
    <p style="font-weight:bold; color:#d3080c;">${getAnswer(lg.status, lang)}</p>

    <h3>Résultat(s)</h3>
    <div>${makeLinksClickable(lg.reason) || "Non disponible"}</div>

    <h3>Informations de contact</h3>
    <div>${makeLinksClickable(getAttr(result, "contact", "fr"))}</div>

    <h3>Comment postuler ou signaler</h3>
    <div>${makeLinksClickable(getAttr(result, "where to apply", "fr"))}</div>

    <h3>Guide d'application et de réglementation</h3>
    <div>${makeLinksClickable(getAttr(result, "guidance", "fr"))}</div>

    <h3>Échéanciers</h3>
    <div>${makeLinksClickable(getAttr(result, "timelines", "fr"))}</div>

    <h3>Exemples de demandes</h3>
    <div>${makeLinksClickable(getAttr(result, "examples", "fr"))}</div>

    <h3>Site Web du programme</h3>
    <div>${makeLinksClickable(getAttr(result, "program website", "fr"))}</div>

    <h3>Législation géographique</h3>
    <p>${makeLinksClickable(getAttr(result, "geographical legislation", "fr"))}</p>

    <h3>Vos réponses aux questions de la recherche guidée</h3>
    <ol>
      ${lg.history?.map((q) => `<li>${q}</li>`).join("") || "<li>Aucune question</li>"}
    </ol>
  </div>
`;

// --------------------------------------------------
// MAIN EXPORT
// --------------------------------------------------
export const downloadResultAsPdfOrDocx = async (
  dataArray,
  type = "pdf",
  language = "en",
) => {

  const isFr = language === "fr";
  const formattedDate = getFormattedDate();
  
  const filename = isFr 
    ? `Navigateur_de_permis_miniers_Resultats_${formattedDate}`
    : `Mine_Permit_Navigator_Results_${formattedDate}`;

  if (type === "pdf") {
    const container = document.createElement("div");

    container.innerHTML = `
      <style>
        * {
          box-sizing: border-box;
        }
        body {
          font-family: Arial, sans-serif;
          line-height: 1.6;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        #pdf-root {
          max-width: 7in;
          margin: 0 auto;
        }
        p, div, li, span, h1, h2, h3, h4, h5, h6, ol, ul, section {
          page-break-inside: avoid;
          break-inside: avoid;
          word-break: normal;
          overflow-wrap: break-word;
          hyphens: none;
          -webkit-hyphens: none;
        }
        h1, h2, h3, h4, h5, h6 {
          page-break-after: avoid;
          page-break-inside: avoid;
        }
        p {
          page-break-inside: avoid;
          orphans: 3;
          widows: 3;
        }
        div[style*="border"] {
          page-break-inside: avoid;
        }
        ol, ul {
          page-break-inside: avoid;
        }
        li {
          page-break-inside: avoid;
        }
        img {
          max-width: 100%;
          height: auto;
        }
      </style>
      <div id="pdf-root">
        ${isFr ? headerFr : headerEn}
        ${isFr ? noticeFr : noticeEn}
        <h1>${isFr ? "Vos résultats" : "Your Results"}</h1>
        ${dataArray.map(lg => isFr ? resultCardHtmlFr(getResultDataForLegislation(lg.name, language),lg, language) : resultCardHtmlEn(getResultDataForLegislation(lg.name, language), lg, language)).join("")}
        ${isFr ? disclaimerFr : disclaimerEn}
      </div>
    `;

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                 (navigator.userAgent.includes("Mac") && navigator.maxTouchPoints > 1);
  const img = new Image();
    img.src = "/static/graphics/Natural_Resources_Canada.png";
    img.onload = () => {
    const pdfWorker = html2pdf()
      .set({
        margin: [0.75, 0.75, 0.75, 0.75],
        filename: `${filename}.pdf`,
        image: { type: "png", quality: 1 },
        html2canvas: {
          scale: 4,
          letterRendering: true,
          useCORS: true,
          logging: false,
          windowHeight: 1056,
          backgroundColor: "#ffffff",
        },
        jsPDF: {
          unit: "in",
          format: "letter",
          compress: false,
          precision: 16,
        },
        pagebreak: {
          mode: ["css", "legacy"],
          avoid: ["p", "h1", "h2", "h3", "li", "div"],
        },
        enableLinks: true,
      })
      .from(container)
      
      if (isIOS) {
      pdfWorker.output('bloburl').then((bloburl) => {
        window.location.href = bloburl;
      });
    } else {
      pdfWorker.save();
    }
  }
  }

  if (type === "docx") {
    const doc = new Document({
      sections: [
        {
          children: [
            new Paragraph({
              text: isFr ? "Vos résultats" : "Your Results",
              heading: "Heading1",
            }),
          ],
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}.docx`;
    link.click();
  }
};